import { useCallback, useEffect, useRef, useState } from "react";
import { CommonSubscriptionDetails, ItemPrice, PriceListType } from "../../../Model/CommonModel";
import { SystemDataLoadingStatus } from "../../../Model/SystemModels";
import { Stack } from "@fluentui/react";
import CommonConfigurationAppGroup from "./CommonConfigurationAppGroup";
import { Row } from "../Container/Grid/Row";
import { Col } from "../Container/Grid/Col";
import { RegisterService } from "../Register/Service/RegisterService";
import { Loader } from "../Loader/Loader";
import { commonConfigurationAppListContainerStyles, commonConfigurationListContainerStyles, commonConfigurationListTotalContainerStyles, commonConfigurationListUserContainerStyles } from "./Styles/CommonConfigurationStyles";
import { SystemCore } from "../../../Core/System/SystemCore";
import { RegisterLoader } from "../Register/RegisterContainer";
import { CommonConfigurationAppTile } from "./CommonConfigurationAppTile";
import { CommonConfigurationPriceList } from "./CommonConfigurationPriceList";
import { CommonConfigurationUserInput } from "./CommonConfigurationUserInput";
import { RegisterConfigurationTotal } from "../Register/Components/Configuration/Components/RegisterConfigurationTotal";

export interface ICommonConfigurationAppListProps {
    isUpdating: boolean;
    commonSubscriptionDetails: CommonSubscriptionDetails[];
    onUpdate: (newSubscriptionDetails: CommonSubscriptionDetails[], fullUsersNo: number) => void;
}

export const CommonConfigurationAppList: React.FC<ICommonConfigurationAppListProps> = (props) => {
    const [dataStatus, setDataStatus] = useState<SystemDataLoadingStatus>(SystemDataLoadingStatus.ToLoad);
    const [fullUsersNo, setFullUsersNo] = useState<number>(props.commonSubscriptionDetails[0].quantity === 0 ? 1 : props.commonSubscriptionDetails[0].quantity);
    const fullUsersNoRef = useRef<number>(props.commonSubscriptionDetails[0].quantity === 0 ? 1 : props.commonSubscriptionDetails[0].quantity);
    const totalAmountRef = useRef<number>(0);
    const [totalAmount, setTotalAmount] = useState<number>(0);
    const [priceListType, setPriceListType] = useState<PriceListType>(props.commonSubscriptionDetails[0].recurringInterval === "year" ? PriceListType.Yearly : PriceListType.Monthly);
    const [currency, setCurrency] = useState<string>("");
    const [apps, setApps] = useState<JSX.Element[]>([]);

    useEffect(() => {
        fullUsersNoRef.current = fullUsersNo;
        calculateTotalAmount();
    }, [fullUsersNo]);

    useEffect(() => {
        buildAppList(priceListType);
    }, [priceListType]);

    const updateQuoteStatus = useCallback((app: CommonSubscriptionDetails, quantity: number, index: number, array: CommonSubscriptionDetails[], isSelected: boolean, priceListType: PriceListType) => {
        let currentappList: CommonSubscriptionDetails[] = array;
        currentappList[index].selected = isSelected;
        currentappList[index].recurringInterval = priceListType;
        currentappList[index].unitPrice = getPrice(app, priceListType);
        if(app.base)
            currentappList[index].quantity = fullUsersNoRef.current;
        else
            currentappList[index].quantity = quantity;

        props.onUpdate(currentappList, fullUsersNoRef.current);
    }, []);

    const buildAppList = (priceListType: PriceListType) => {
        setDataStatus(SystemDataLoadingStatus.Loading);
    
        const groupedApps: Record<string, CommonSubscriptionDetails[]> = {};
        props.commonSubscriptionDetails.forEach((app) => {
            const groupKey = app.group || "main";
            if (!groupedApps[groupKey]) {
                groupedApps[groupKey] = [];
            }
            groupedApps[groupKey].push(app);
        });
    
        let appList: JSX.Element[] = [];
    
        Object.entries(groupedApps).forEach(([groupKey, appsInGroup]) => {
            appList.push(
                <Stack key={groupKey}>
                    <CommonConfigurationAppGroup title={groupKey} />
                    <Row>
                        {appsInGroup.map((app) => {
                            if (app.hidden && !app.selected)
                                return null;
                            
                            const currentQuantity = app.base ? fullUsersNoRef.current : app.quantity;

                            const globalIndex = props.commonSubscriptionDetails.findIndex(a => a.itemId === app.itemId);

                            const element: JSX.Element = 
                                <Col key={app.itemId}>
                                    <CommonConfigurationAppTile
                                        app={app}
                                        priceString={buildPriceString(app)}
                                        onChange={(isSelected, quantity) => {
                                            if (app.hidden) return;
                                            
                                            if (app.mandatory && !isSelected) return;
    
                                            updateQuoteStatus(
                                                app,
                                                quantity,
                                                globalIndex,
                                                props.commonSubscriptionDetails,
                                                isSelected,
                                                priceListType
                                            );
    
                                            buildAppList(priceListType);
                                        }}
                                    />
                                </Col>
                                
                                if (app.mandatory || app.selected)
                                    updateQuoteStatus(app, currentQuantity, globalIndex, props.commonSubscriptionDetails, app.selected, priceListType);
                                
                                return element;
                        })}
                    </Row>
                </Stack>
            );
        });
    
        setApps(appList);
        calculateTotalAmount();
        setDataStatus(SystemDataLoadingStatus.Loaded);
    };
    
    const getPrice = useCallback((currentApp: CommonSubscriptionDetails, priceListType: PriceListType) : number => {
        if(currentApp.prices.length > 0) {
            let price: ItemPrice | undefined = currentApp.prices.find((price: ItemPrice) => price.interval === priceListType);
            
            if (!price)
                price = currentApp.prices.find((price: ItemPrice) => price.type === PriceListType.OneTime);

            if(price)
                return price.unitAmount;
        }
        
        return 0;
    }, []);

    const buildPriceString = (currentApp: CommonSubscriptionDetails) : string => {
        let priceString: string = "";
        let currCurrency: string = "";

        if(currentApp.prices.length > 0) {
            let price: ItemPrice | undefined = currentApp.prices.find((price: ItemPrice) => price.interval === priceListType);

            if (!price)
                price = currentApp.prices.find((price: ItemPrice) => price.type === PriceListType.OneTime);

            if(price) {
                priceString = price.unitAmount.toString();
                currCurrency = price.currency;
            }
        }
        else
            priceString = currentApp.unitPrice.toString();

        switch(currCurrency) {
            case 'eur':
                priceString += "€";
                if(currency === "")
                    setCurrency("€");
                break;
            case 'usd':
                priceString += "$";
                if(currency === "")
                    setCurrency("$");
                break;
        }

        if(currentApp.base) {
            switch(priceListType) {
                case PriceListType.Yearly:
                    priceString += RegisterService.translate("configuration:userYear");
                    break;
                case PriceListType.Monthly:
                    priceString += RegisterService.translate("configuration:userMonth");
                    break;
            }
        }

        return priceString;
    }

    const calculateTotalAmount = () => {
        let selectedApps: CommonSubscriptionDetails[] = props.commonSubscriptionDetails.filter((app: CommonSubscriptionDetails) => app.selected);
        let total: number = 0;
        
        selectedApps.forEach((app: CommonSubscriptionDetails, index: number) => {
            const globalIndex = props.commonSubscriptionDetails.findIndex(a => a.itemId === app.itemId);
            updateQuoteStatus(app, app.quantity, globalIndex, props.commonSubscriptionDetails, app.selected, priceListType);
            let price: ItemPrice | undefined = app.prices.find((price: ItemPrice) => price.interval === priceListType);

            if (!price)
                price = app.prices.find((price: ItemPrice) => price.type === PriceListType.OneTime);

            if(price) {
                if(app.base)
                    total += price.unitAmount * fullUsersNoRef.current;
                else
                    total += price.unitAmount * app.quantity;
            }
            else {
                if(app.base)
                    total += app.unitPrice * fullUsersNoRef.current;
                else
                    total += app.unitPrice * app.quantity;
            }
        });

        totalAmountRef.current = total;
        setTotalAmount(total);
    }

    const validatePriceList = (priceListType: PriceListType) => {
        let selectedApps: CommonSubscriptionDetails[] = props.commonSubscriptionDetails;

        let newSelectedApps: CommonSubscriptionDetails[] = selectedApps;
        selectedApps.forEach((app: CommonSubscriptionDetails, index: number, array: CommonSubscriptionDetails[]) => {
            newSelectedApps[index].recurringInterval = priceListType
        });
        
        props.onUpdate(newSelectedApps, fullUsersNoRef.current);

        setPriceListType(priceListType);
    }
    
    return (
        props.isUpdating ? <Loader text={RegisterService.translate("configuration:loader")} />
        :
        <Stack
            verticalFill
            verticalAlign="space-between"
            className={commonConfigurationListContainerStyles}
        >
            <Stack
                horizontal
                horizontalAlign="space-between"
                verticalAlign="center"
                verticalFill
                className={commonConfigurationListUserContainerStyles}
            >
                <CommonConfigurationPriceList
                    priceListType={priceListType}
                    onChange={(priceListType: PriceListType) => {validatePriceList(priceListType)}}
                />
                <CommonConfigurationUserInput
                    userNo={fullUsersNo}
                    onChange={(userNo) => {userNo >= 1 && setFullUsersNo(userNo)}}
                />
            </Stack>
            {
                !SystemCore.isFinishedDataStatus(dataStatus) ?
                    <RegisterLoader 
                        label={RegisterService.translate("configuration:loader")}
                    />
                :
                    <Stack
                        className={commonConfigurationAppListContainerStyles}
                    >
                        {apps}
                    </Stack>
            }
            <Stack
                horizontal
                horizontalAlign="end"
                verticalAlign="center"
                verticalFill
                className={commonConfigurationListTotalContainerStyles}
            >
                <RegisterConfigurationTotal 
                    total={totalAmount}
                    currency={currency}
                />             
            </Stack>
        </Stack>
    );
};